import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, SegmentedControl, Select, Switch, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const KINDS: admin.AddCreditKind[] = ['payment', 'gift', 'correction'];
const METHODS = ['eft', 'cash', 'other'] as const;
const MAX_RAND = 1_000_000;

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Adding credit by hand (owner, 15 October 2026: "a way that I can credit the tenant's credit ... also used if a
 * client is on manual invoicing"). A payment made outside the platform (EFT on a manual invoice, cash) is a top-up
 * and the business gets its usual receipt; a gift is credit the platform gives; a correction takes credit back, never
 * below nothing. Every one confirms first, naming the business and the balance before and after, and the form sends
 * one request id, so a double click adds the credit once.
 */
export function AddCreditDrawer({ opened, onClose, organisationId, organisationName, balanceMinor, currency }: {
  opened: boolean;
  onClose: () => void;
  organisationId: string;
  organisationName: string;
  balanceMinor: number;
  currency: string;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [kind, setKind] = useState<admin.AddCreditKind>('payment');
  const [rand, setRand] = useState<number | ''>('');
  const [method, setMethod] = useState<(typeof METHODS)[number]>('eft');
  const [reference, setReference] = useState('');
  const [receivedOn, setReceivedOn] = useState(today);
  const [reason, setReason] = useState('');
  const [receipt, setReceipt] = useState(true);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [refusal, setRefusal] = useState<{ field: string | null; message: string } | null>(null);

  // A fresh form each time it opens: a new request id, so this is a new credit and not a retry of the last one.
  useEffect(() => {
    if (!opened) return;
    setKind('payment'); setRand(''); setMethod('eft'); setReference(''); setReceivedOn(today()); setReason('');
    setReceipt(true); setRequestId(crypto.randomUUID()); setRefusal(null);
  }, [opened]);

  const minor = typeof rand === 'number' ? Math.round(rand * 100) : 0;
  const after = kind === 'correction' ? balanceMinor - minor : balanceMinor + minor;

  const amountError = rand === '' ? undefined
    : minor < 100 || rand > MAX_RAND ? t('tenants.view.credit.amountInvalid')
    : kind === 'correction' && minor > balanceMinor ? t('tenants.view.credit.moreThanBalance', { balance: formatMinor(Math.max(0, balanceMinor), currency) })
    : undefined;
  const referenceError = kind === 'payment' && reference.trim().length > 0 && reference.trim().length < 2 ? t('tenants.view.credit.referenceInvalid') : undefined;
  const reasonError = kind !== 'payment' && reason.trim().length > 0 && reason.trim().length < 3 ? t('tenants.view.credit.reasonInvalid') : undefined;
  const dateError = kind === 'payment' && receivedOn > today() ? t('tenants.view.credit.dateFuture') : undefined;
  const ready = minor >= 100 && !amountError && !dateError
    && (kind === 'payment' ? reference.trim().length >= 2 && !!receivedOn : reason.trim().length >= 3);

  const save = useMutation({
    mutationFn: () => admin.addCredit(organisationId, {
      kind, amount_minor: minor, request_id: requestId,
      method: kind === 'payment' ? method : null,
      reference: kind === 'payment' ? reference.trim() : null,
      received_on: kind === 'payment' ? receivedOn : null,
      reason: kind === 'payment' ? null : reason.trim(),
      send_receipt: kind === 'payment' ? receipt : null
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'wallet-alert', organisationId] });
      qc.invalidateQueries({ queryKey: ['admin', 'ledger'] });
      const r = res.data;
      notifySuccess(
        t(`tenants.view.credit.done.${r.kind}`, { amount: formatMinor(r.amount_minor, currency), name: organisationName, balance: formatMinor(r.balance_minor, currency) })
          + (r.receipt_sent ? ` ${t('tenants.view.credit.done.receipt')}` : ''),
        t('tenants.view.credit.title'));
      onClose();
    },
    onError: (e) => {
      const err = isApiError(e) ? (e.body as { error?: { field?: string; message?: string } } | null)?.error : null;
      setRefusal({ field: err?.field ?? null, message: err?.message ?? (e instanceof Error ? e.message : t('common.unknownError')) });
    }
  });

  const confirmBody = useMemo(() => (
    <Stack gap="sm">
      <Text role="bodySmall">
        {t(`tenants.view.credit.confirm.${kind}`, { amount: formatMinor(minor, currency), name: organisationName, reference: reference.trim() })}
      </Text>
      <Text role="bodySmall" fw={600}>
        {t('tenants.view.credit.confirm.balance', { before: formatMinor(balanceMinor, currency), after: formatMinor(after, currency) })}
      </Text>
      {kind === 'payment' && receipt && <Text role="caption" c="var(--rr-color-text-muted)">{t('tenants.view.credit.confirm.receipt')}</Text>}
      {kind !== 'payment' && <Text role="caption" c="var(--rr-color-text-muted)">{t('tenants.view.credit.confirm.ledger', { reason: reason.trim() })}</Text>}
    </Stack>
  ), [t, kind, minor, currency, organisationName, reference, balanceMinor, after, receipt, reason]);

  function submit() {
    if (!ready || save.isPending) return;
    setRefusal(null);
    openConfirmModal({
      title: t(`tenants.view.credit.confirm.title.${kind}`, { name: organisationName }),
      children: confirmBody,
      labels: { confirm: t(`tenants.view.credit.confirm.submit.${kind}`), cancel: t('action.cancel') },
      confirmProps: { color: kind === 'correction' ? 'red' : 'teal' },
      onConfirm: () => save.mutate()
    });
  }

  const errorFor = (field: string, local?: string) => (refusal?.field === field ? refusal.message : local);

  return (
    <Drawer opened={opened} onClose={onClose} title={t('tenants.view.credit.title')} position="right" size="md">
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('tenants.view.credit.intro', { name: organisationName, balance: formatMinor(balanceMinor, currency) })}
        </Text>
        <Stack gap={6}>
          <Text role="inputLabel">{t('tenants.view.credit.kind')}</Text>
          <SegmentedControl fullWidth value={kind} onChange={(v) => { setKind(v as admin.AddCreditKind); setRefusal(null); }}
            data={KINDS.map((k) => ({ value: k, label: t(`tenants.view.credit.kind.${k}`) }))} />
          <Text role="caption" c="var(--rr-color-text-muted)">{t(`tenants.view.credit.kindHint.${kind}`)}</Text>
        </Stack>
        <NumberInput
          label={t(kind === 'correction' ? 'tenants.view.credit.amountTake' : kind === 'payment' ? 'tenants.view.credit.amountPaid' : 'tenants.view.credit.amount')}
          value={rand}
          onChange={(v) => { setRand(typeof v === 'number' ? v : ''); setRefusal(null); }}
          min={1}
          step={0.01}
          required
          description={t('tenants.view.credit.amountHint')}
          error={errorFor('amount_minor', amountError)}
          data-testid="credit-amount"
        />
        {kind === 'payment' && (
          <>
            <Select label={t('tenants.view.credit.method')} value={method} onChange={(e) => setMethod(e.currentTarget.value as (typeof METHODS)[number])}
              data={METHODS.map((m) => ({ value: m, label: t(`tenants.view.credit.method.${m}`) }))} />
            <TextInput label={t('tenants.view.credit.reference')} value={reference} required maxLength={80}
              onChange={(e) => { setReference(e.currentTarget.value); setRefusal(null); }}
              description={t('tenants.view.credit.referenceHint')} error={errorFor('reference', referenceError)} data-testid="credit-reference" />
            <TextInput type="date" label={t('tenants.view.credit.receivedOn')} value={receivedOn} required max={today()}
              onChange={(e) => setReceivedOn(e.currentTarget.value)} error={errorFor('received_on', dateError)} />
            <Switch label={t('tenants.view.credit.receipt')} description={t('tenants.view.credit.receiptHint')} checked={receipt} onChange={setReceipt} />
          </>
        )}
        {kind !== 'payment' && (
          <Textarea label={t('tenants.view.credit.reason')} value={reason} required maxLength={200} autosize minRows={2}
            onChange={(e) => { setReason(e.currentTarget.value); setRefusal(null); }}
            description={t(`tenants.view.credit.reasonHint.${kind}`)} error={errorFor('reason', reasonError)} data-testid="credit-reason" />
        )}
        {minor >= 100 && !amountError && (
          <Text role="bodySmall" data-testid="credit-after">
            {t('tenants.view.credit.after', { before: formatMinor(balanceMinor, currency), after: formatMinor(after, currency) })}
          </Text>
        )}
        {refusal && !['amount_minor', 'reference', 'received_on', 'reason'].includes(refusal.field ?? '') && (
          <Alert color="error" title={t('tenants.view.credit.failed')}>{refusal.message}</Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={submit} disabled={!ready} loading={save.isPending} color={kind === 'correction' ? 'red' : undefined} data-testid="credit-save">
            {t(`tenants.view.credit.save.${kind}`)}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
